import { useEffect, useRef } from "react";
import { ActionButtonGroup } from "@doculegis/shared";
import { FileTypeIcon } from "@/apps/rag/components/file-icons/FileTypeIcon";
import { cn } from "@/lib/utils";
import { RotateCw } from "lucide-react";
import {
  hasAnyDocumentActions,
  RagDocumentActions,
  type RagDocumentActionCallbacks,
} from "./RagDocumentActions.js";
import type { DocumentStatus } from "../types.js";

const STATUS_STYLE: Record<DocumentStatus, { label: string; dot: string; text: string }> = {
  completed: {
    label: "Ready",
    dot: "bg-[var(--tint-green-fg)]",
    text: "text-[var(--tint-green-fg)]",
  },
  processing: {
    label: "Processing",
    dot: "animate-pulse bg-[var(--comment-fg)]",
    text: "text-[var(--comment-fg)]",
  },
  pending: {
    label: "Queued",
    dot: "bg-[var(--tint-amber-fg)]",
    text: "text-[var(--tint-amber-fg)]",
  },
  failed: {
    label: "Failed",
    dot: "bg-[var(--tint-red-fg)]",
    text: "text-[var(--tint-red-fg)]",
  },
};

export type RagDocumentCardItem = {
  id: string;
  title: string;
  type: string;
  size: string;
  updatedAt: string;
  status: DocumentStatus;
};

type Props = RagDocumentActionCallbacks & {
  document: RagDocumentCardItem;
  className?: string;
  onDetails?: (documentId: string) => void;
};

function splitFileName(name: string, type: string) {
  const dotIndex = name.lastIndexOf(".");
  if (dotIndex > 0) {
    return { baseName: name.slice(0, dotIndex), extension: name.slice(dotIndex + 1).toLowerCase() };
  }
  const extension = type.trim().toLowerCase();
  return { baseName: name, extension };
}

export function RagDocumentCard({
  document: doc,
  onView,
  onReprocess,
  onDelete,
  onStatus,
  className,
  onDetails,
}: Props) {
  const opensDetails = Boolean(onDetails);
  const clickTimerRef = useRef<number | null>(null);
  const showActions = hasAnyDocumentActions({ onView, onReprocess, onDelete, onStatus });
  const status = STATUS_STYLE[doc.status];
  const { baseName, extension } = splitFileName(doc.title, doc.type);
  const isFailed = doc.status === "failed";
  const interactive = opensDetails || (doc.status === "completed" && Boolean(onView));

  useEffect(() => {
    return () => {
      if (clickTimerRef.current !== null) {
        window.clearTimeout(clickTimerRef.current);
      }
    };
  }, []);

  function openDetails() {
    if (onDetails) {
      onDetails(doc.id);
      return;
    }
    if (doc.status === "completed" && onView) {
      onView(doc.id);
    } else if ((doc.status === "processing" || doc.status === "pending") && onStatus) {
      onStatus(doc.id);
    }
  }

  function handleTileClick() {
    if (!onDetails) {
      openDetails();
      return;
    }
    if (clickTimerRef.current !== null) {
      window.clearTimeout(clickTimerRef.current);
    }
    clickTimerRef.current = window.setTimeout(() => {
      clickTimerRef.current = null;
      openDetails();
    }, 250);
  }

  function handleTileDoubleClick() {
    if (!onView) return;
    if (clickTimerRef.current !== null) {
      window.clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    onView(doc.id);
  }

  return (
    <article
      className={cn(
        "group relative flex flex-col gap-3 rounded-xl border border-[var(--lm-border-card)] bg-white p-3 shadow-[var(--shadow-card)] transition-colors hover:bg-slate-50 dark:border-white/10 dark:bg-transparent dark:shadow-none dark:hover:border-white/15 dark:hover:bg-white/[0.04]",
        isFailed && "border-rose-200 bg-rose-50 hover:bg-rose-50 dark:border-rose-500/20 dark:bg-rose-500/5 dark:hover:bg-rose-500/5",
        interactive && "cursor-pointer",
        className,
      )}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={handleTileClick}
      onDoubleClick={handleTileDoubleClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openDetails();
        }
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <FileTypeIcon type={doc.type} extension={extension} />
        <div
          className="-mr-1 -mt-1 flex items-center"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {isFailed && onReprocess ? (
            <button
              type="button"
              title="Retry"
              aria-label="Retry"
              onClick={() => onReprocess(doc.id)}
              className="inline-flex size-7 items-center justify-center rounded-lg text-rose-600 transition-colors hover:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/15"
            >
              <RotateCw className="size-3.5" aria-hidden />
            </button>
          ) : null}
          {showActions ? (
            <ActionButtonGroup className="pointer-events-auto">
              <RagDocumentActions
                status={doc.status}
                documentId={doc.id}
                onView={onView}
                onReprocess={onReprocess}
                onDelete={onDelete}
                onStatus={onStatus}
                menuSize="sm"
                emptyFallback={null}
              />
            </ActionButtonGroup>
          ) : null}
        </div>
      </div>

      <div className="min-w-0 space-y-1">
        <p className="truncate text-[13px] font-medium text-slate-900 dark:text-slate-100" title={doc.title}>
          {baseName}
          {extension ? <span className="text-slate-500">.{extension}</span> : null}
        </p>
        <span className={cn("inline-flex min-w-0 items-center gap-1.5 text-xs font-medium", status.text)}>
          <span className={cn("size-1.5 shrink-0 rounded-full", status.dot)} aria-hidden />
          <span className="truncate">{status.label}</span>
        </span>
        <p className="flex items-center gap-1.5 truncate text-[11px] text-slate-500">
          <span className="tabular-nums">{doc.size}</span>
          <span aria-hidden>·</span>
          <span className="truncate">{doc.updatedAt}</span>
        </p>
      </div>
    </article>
  );
}
